import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import SeoHead, { orgSchema, websiteSchema, breadcrumbSchema } from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import AuthorBio from "@/components/AuthorBio";
import { SITE, MARK } from "@/lib/seoData";
import { Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny markdown → HTML renderer (safe-enough for trusted admin-authored copy).
// Supports: ## headings, ### subheadings, **bold**, lists (- ), and paragraphs.
function md(src) {
  if (!src) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = src.split(/\n{2,}/);
  return blocks.map((block) => {
    const t = block.trim();
    if (t.startsWith("## ")) return `<h2>${escape(t.slice(3))}</h2>`;
    if (t.startsWith("### ")) return `<h3>${escape(t.slice(4))}</h3>`;
    if (/^[-*]\s/.test(t)) {
      const items = t.split(/\n/).map((l) => `<li>${escape(l.replace(/^[-*]\s/, ""))}</li>`).join("");
      return `<ul>${items}</ul>`;
    }
    return `<p>${escape(t).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")}</p>`;
  }).join("\n");
}

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/insights" replace />;

  const path = `/insights/${slug}`;
  const articleSchema = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    ...(post.image ? { image: [post.image] } : {}),
    author: {
      "@type": "Person",
      "@id": MARK.personId,
      name: MARK.name,
      url: `${SITE.url}${MARK.path}`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE.url}/#organization`,
      name: SITE.name,
      logo: { "@type": "ImageObject", url: SITE.logo },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}${path}` },
    keywords: (post.keywords || []).join(", "),
  } : null;

  return (
    <div className="bg-[#050505] text-white font-outfit min-h-screen">
      <SeoHead
        title={post?.title || "Insight"}
        description={post?.excerpt || "Australian finance insight from OzyLoans."}
        path={path}
        schemas={post ? [
          orgSchema(),
          websiteSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: post.title, path },
          ]),
          articleSchema,
        ] : []}
      />
      <SiteHeader />
      <article className="px-6 pt-16 pb-20 max-w-3xl mx-auto" data-testid="insight-article">
        <Link to="/insights" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-white text-xs uppercase tracking-widest mb-8">
          <ArrowLeft className="w-3 h-3" /> All insights
        </Link>

        {loading && (
          <div className="text-zinc-500 flex items-center gap-2 py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && post && (
          <>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] mb-4 font-semibold">{post.category}</p>
            <h1 className="font-bebas text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] mb-5">{post.title}</h1>
            <div className="mb-10">
              <AuthorBio author={MARK} datePublished={post.created_at} dateModified={post.updated_at} />
            </div>
            {post.excerpt && (
              <p className="text-zinc-300 text-lg leading-relaxed mb-8 border-l-2 border-[#E50914] pl-5">{post.excerpt}</p>
            )}
            <div
              className="prose prose-invert max-w-none prose-headings:font-bebas prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-zinc-300 prose-p:leading-relaxed prose-li:text-zinc-300 prose-strong:text-white prose-a:text-[#E50914] prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(md(post.body_md), {
                  ALLOWED_TAGS: ["h2", "h3", "p", "ul", "li", "strong", "em", "a", "br"],
                  ALLOWED_ATTR: ["href", "rel", "target"],
                }),
              }}
            />
            <div className="mt-14 p-6 rounded-2xl border border-zinc-800 bg-[#0a0a0a] text-sm text-zinc-400">
              <p className="text-white font-semibold mb-1.5">General-information disclaimer</p>
              <p className="leading-relaxed text-[12.5px]">
                This article is general information only and does not constitute financial, legal or tax advice. It does not take into account your personal objectives, financial situation or needs. All finance is subject to lender approval, responsible-lending obligations and your individual circumstances. Speak with the OzyLoans team or a qualified adviser before making any financial decision. OzyLoans · ACL #485752 · AFCA Member #618333.
              </p>
            </div>
          </>
        )}
      </article>
      <SiteFooter />
    </div>
  );
}
